import type { TabView } from "@e308/ux";
import type { ConflictEvent } from "../types.js";
import type { AppIntent, AppState, LogFilter } from "./app-source.js";
import {
  formatGameClock as clock,
  heading,
  type AppNode as Node,
  name,
  text,
} from "./view-nodes.js";

export function logTab(state: AppState): TabView<AppIntent, string> {
  const events = state.events.filter((event) => matches(event, state.logFilter));
  return {
    id: "shift-log",
    label: "Shift Log",
    content: [
      heading("shift-log-heading", "Shift Log"),
      {
        kind: "select-input",
        id: "log-filter",
        label: "Filter",
        value: state.logFilter,
        options: ["all", "combat", "diplomacy", "production", "incidents"].map((value) => ({
          value,
          label: name(value),
        })),
        intent: (value) => ({ kind: "set-log-filter", value: value as LogFilter }),
      },
      ...(events.length === 0
        ? [text("log-empty", "No matching committed events yet.")]
        : events.slice().reverse().map(eventNode)),
    ],
  };
}

export function resultsView(state: AppState): readonly Node[] {
  const observation = state.observation;
  if (!observation || observation.match.status !== "ended") return [];
  const outcome = observation.match.outcome;
  const winner = outcome?.winnerId
    ? (observation.seats.find((seat) => seat.id === outcome.winnerId)?.name ??
      name(outcome.winnerId))
    : "No town";
  const damageWindows = state.events.filter((event) => event.kind === "damage-summary").length;
  const shipments = observation.diplomacy.shipments.filter(
    (entry) => entry.status === "delivered",
  ).length;
  return [
    heading("results-heading", "Shift result"),
    {
      kind: "notification",
      id: "result-outcome",
      tone: outcome?.kind === "draw" ? "warning" : "positive",
      text:
        outcome?.kind === "draw"
          ? "The shift ended in a draw."
          : `${winner} won the ${name(outcome?.kind ?? "result")}.`,
    },
    text(
      "result-duration",
      `Final time ${clock(observation.gameTimeMs)}. ${observation.match.eliminated.length} towns eliminated, ${shipments} shipments delivered, ${damageWindows} damage report windows.`,
    ),
    ...observation.seats.map((seat) =>
      text(
        `result-${seat.id}`,
        `${seat.name}: Headquarters ${seat.headquartersIntegrity}, Metal ${seat.metal}, ${seat.research.completed.length} technologies completed, ${Object.values(seat.units).join(" / ")} reserve units.`,
      ),
    ),
    ...(state.replayActive
      ? [
          {
            kind: "notification",
            id: "replay-mode",
            tone: "warning",
            text: "Inspecting a deterministic replay result. The retained live result is unchanged.",
          } as Node,
        ]
      : []),
  ];
}

function eventNode(event: ConflictEvent): Node {
  return text(`log-${event.sequence}`, `${clock(event.atGameMs)} — ${eventText(event)}`);
}

function eventText(event: ConflictEvent): string {
  if (event.kind === "shield-breached")
    return `${name(event.seatId ?? "town")} shield breached at ${name(event.borderId ?? "border")}.`;
  if (event.kind === "seat-eliminated") return `${name(event.seatId ?? "town")} eliminated.`;
  if (event.kind === "new-border")
    return `New border with ${name(event.borderId ?? "neighbor")}; peace until ${clock(event.fightingStartsAtMs ?? event.atGameMs)}.`;
  if (event.kind === "overtime-started") return "Overtime started.";
  if (event.kind === "match-ended")
    return event.outcome === "draw"
      ? "Match ended in a draw."
      : `Match ended; ${name(event.winnerId ?? "town")} won.`;
  if (event.kind === "production-milestone")
    return `${name(event.seatId)} completed milestone ${name(event.itemId)}.`;
  if (event.kind === "controller-changed")
    return `${name(event.seatId)} controller changed to ${name(event.mode)} (generation ${event.generation}).`;
  if (event.kind === "room-pause-changed")
    return event.paused
      ? "Room paused by unanimous agreement."
      : "Room resumed by unanimous agreement.";
  if (event.kind === "damage-summary")
    return `${event.entries.length} combat damage reports committed.`;
  if ("actorId" in event)
    return `${name(event.kind)}: ${name(event.actorId)} and ${name(event.targetId)}.`;
  if ("senderId" in event)
    return `${name(event.kind)}: ${event.amount} Metal from ${name(event.senderId)} to ${name(event.recipientId)}.`;
  if ("incident" in event)
    return `${name(event.kind)}: ${name(event.incident.id)}${event.incident.targetId ? ` at ${name(event.incident.targetId)}` : ""}.`;
  return name(event.kind);
}

function matches(event: ConflictEvent, filter: LogFilter): boolean {
  if (filter === "all") return true;
  if (filter === "combat")
    return [
      "shield-breached",
      "damage-summary",
      "seat-eliminated",
      "new-border",
      "match-ended",
      "overtime-started",
    ].includes(event.kind);
  if (filter === "diplomacy")
    return event.kind.startsWith("pact-") || event.kind.startsWith("metal-");
  if (filter === "production") return event.kind === "production-milestone";
  return event.kind.startsWith("incident-");
}
